import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiService from '../services/apiService';
import { telegramEscopoHint, telegramEscopoTexto, MOSTRAR_FREQUENCIA, MODO_PADRAO } from '../utils/telegramEscopo';
import './UserCreate.css';

const maskDate = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 8);
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};
const brToISO = (br) => { const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/); return m ? `${m[3]}-${m[2]}-${m[1]}` : ''; };
const isoToBR = (iso) => { if (!iso) return ''; const p = iso.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : ''; };

const DateInputBR = ({ value, onChange, id }) => {
    const ref = useRef(null);
    return (
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <input
                id={id}
                type="text"
                value={value}
                onChange={(e) => onChange(maskDate(e.target.value))}
                className="form-input"
                placeholder="dd/mm/aaaa"
                maxLength={10}
                style={{ flex: 1 }}
            />
            <input
                ref={ref}
                type="date"
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, overflow: 'hidden' }}
                value={brToISO(value)}
                onChange={(e) => onChange(isoToBR(e.target.value))}
            />
            <button
                type="button"
                onClick={() => ref.current?.showPicker?.()}
                style={{ background: 'none', border: '1px solid #ccc', borderRadius: '6px', padding: '6px 10px', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}
                title="Abrir calendário"
            >
                📅
            </button>
        </div>
    );
};

const UserCreate = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { company, store, fixedRole, scrollY } = location.state || {};
    const backUrl = scrollY ? `/users?scrollY=${scrollY}` : '/users';

    const [fullname, setFullname] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState(fixedRole || 'salesman');
    const [appVersionName, setAppVersionName] = useState('default');
    const [recordingsStart, setRecordingsStart] = useState('');
    const [feedbackReadEnabled, setFeedbackReadEnabled] = useState(false);
    // WhatsApp: é por este telefone que o relatório pedido por mensagem encontra a pessoa.
    // Aceita o que for digitado — quem normaliza para o formato da Meta é a API.
    const [whatsappPhone, setWhatsappPhone] = useState('');
    // Opt-in: sem ele nao ha PUSH (a Meta exige consentimento registrado para template). O
    // pull nao depende disto -- quem escreve pedindo relatorio consentiu pelo ato.
    const [whatsappEnabled, setWhatsappEnabled] = useState(false);
    // Telegram: vale para qualquer papel (a chefia é boa parte dos destinatários).
    const [telegramUsername, setTelegramUsername] = useState('');
    const [feedbackTelegramEnabled, setFeedbackTelegramEnabled] = useState(false);
    // 'digest' é o default de propósito: um diretor em 'each' levaria ~111 mensagens/dia.
    const [feedbackTelegramMode, setFeedbackTelegramMode] = useState(MODO_PADRAO);
    // Curto ligado por padrão (é o que todos recebem); longo desligado.
    const [feedbackTelegramShort, setFeedbackTelegramShort] = useState(true);
    const [feedbackTelegramLong, setFeedbackTelegramLong] = useState(false);
    const [feedbackReadStartDate, setFeedbackReadStartDate] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [passwordMismatch, setPasswordMismatch] = useState(false);

    const allRoles = [
        { value: 'admin', label: 'Administrador' },
        { value: 'salesman', label: 'Vendedor' },
        { value: 'manager', label: 'Gerente' },
        { value: 'supervisor', label: 'Supervisor' },
        // 'director' existia no banco (32 em produção) mas não nesta lista — não havia como
        // criar nem editar um diretor pela tela. Ele recebe feedback da EMPRESA toda.
        { value: 'director', label: 'Diretor' },
    ];

    const roles = fixedRole
        ? allRoles.filter((r) => r.value === fixedRole)
        : allRoles;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: '', text: '' });

        if (!fullname.trim() || !username.trim() || !password || !confirmPassword) {
            setMessage({ type: 'error', text: 'Preencha todos os campos.' });
            return;
        }

        if (password !== confirmPassword) {
            setMessage({ type: 'error', text: 'As senhas não coincidem.' });
            return;
        }

        if (password.length < 4) {
            setMessage({ type: 'error', text: 'A senha deve ter pelo menos 4 caracteres.' });
            return;
        }

        // A recorder-api recusa salesman sem recordings_start (400), então barrar aqui
        // em vez de deixar a pessoa descobrir por uma mensagem de erro da API.
        if (role === 'salesman' && !brToISO(recordingsStart)) {
            setMessage({ type: 'error', text: 'Informe a Data de Início (dd/mm/aaaa) do vendedor.' });
            return;
        }

        try {
            setSubmitting(true);
            const payload = {
                fullname: fullname.trim(),
                username: username.trim(),
                password,
                company_id: company?.id,
                store_ids: String(store?.id),
                role,
            };
            if (role === 'salesman') {
                payload.recordings_start = brToISO(recordingsStart);
            }
            // app_version_name só é enviado quando o usuário escolheu (e é salesman)
            if (role === 'salesman' && appVersionName) {
                payload.app_version_name = appVersionName;
            }
            payload.feedback_read_enabled = feedbackReadEnabled;
            if (whatsappPhone.trim()) {
                payload.whatsapp_phone = whatsappPhone.trim();
                // Sem telefone o opt-in nao significa nada -- nao mandar evita gravar um
                // consentimento que nao alcanca ninguem.
                payload.whatsapp_enabled = whatsappEnabled;
            }
            if (telegramUsername.trim()) {
                payload.telegram_username = telegramUsername.trim();
            }
            payload.feedback_telegram_enabled = feedbackTelegramEnabled;
            if (feedbackTelegramEnabled) {
                payload.feedback_telegram_mode = MOSTRAR_FREQUENCIA ? feedbackTelegramMode : MODO_PADRAO;
                payload.feedback_telegram_short = feedbackTelegramShort;
                payload.feedback_telegram_long = feedbackTelegramLong;
            }
            if (feedbackReadEnabled && feedbackReadStartDate) {
                const iso = brToISO(feedbackReadStartDate);
                if (iso) payload.feedback_read_enabled_start_date = iso;
            }
            const result = await apiService.registerUser(payload);

            if (result.success) {
                setMessage({ type: 'success', text: `Usuário "${fullname.trim()}" cadastrado com sucesso!` });
                setFullname('');
                setUsername('');
                setPassword('');
                setConfirmPassword('');
                setRole('salesman');
                setAppVersionName('default');
                setRecordingsStart('');
                setFeedbackReadEnabled(false);
                setFeedbackReadStartDate('');
                // Limpar o telefone é obrigatório, não cosmético: sem isto o próximo
                // cadastro herda o número do anterior, e o relatório de uma pessoa vai
                // para o WhatsApp de outra.
                setWhatsappPhone('');
                setWhatsappEnabled(false);
                setTelegramUsername('');
                setFeedbackTelegramEnabled(false);
                setFeedbackTelegramMode(MODO_PADRAO);
            } else {
                const detail = JSON.stringify(result.details || '');
                if (detail.includes('duplicate key') || detail.includes('Username já existe')) {
                    setMessage({ type: 'error', text: 'Este nome de usuário já existe. Escolha outro.' });
                } else {
                    setMessage({ type: 'error', text: result.message || 'Erro ao cadastrar usuário.' });
                }
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Erro ao cadastrar usuário. Tente novamente.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="user-create">
            <div className="user-create-header">
                <button className="btn-back" onClick={() => navigate(backUrl)}>
                    &#8592; Voltar
                </button>
                <h1>{fixedRole === 'salesman' ? 'Cadastrar Vendedor' : 'Cadastrar Usuário'}</h1>
            </div>

            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    {message.text}
                </div>
            )}

            <form className="user-create-form" onSubmit={handleSubmit}>
                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="company">Empresa</label>
                        <input
                            id="company"
                            type="text"
                            value={company?.name || ''}
                            disabled
                            className="form-input"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="store">Loja</label>
                        <input
                            id="store"
                            type="text"
                            value={store?.name || ''}
                            disabled
                            className="form-input"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="fullname">Nome Completo</label>
                        <input
                            id="fullname"
                            type="text"
                            value={fullname}
                            onChange={(e) => setFullname(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome completo"
                            autoFocus
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="username">Usuário (login)</label>
                        <input
                            id="username"
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="form-input"
                            placeholder="Digite o nome de usuário"
                        />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="whatsappPhone">WhatsApp <span className="field-optional">(opcional)</span></label>
                        <input
                            id="whatsappPhone"
                            type="tel"
                            value={whatsappPhone}
                            onChange={(e) => setWhatsappPhone(e.target.value)}
                            className="form-input"
                            placeholder="(19) 98113-1421"
                        />
                        <small className="field-hint">
                            Com DDD. Pode digitar com parênteses e traço — o sistema arruma.
                            É por ele que a pessoa pede o relatório pelo WhatsApp.
                        </small>
                    </div>
                    <div className="form-group">
                        <label htmlFor="whatsappEnabled">Receber relatórios por WhatsApp?</label>
                        <select
                            id="whatsappEnabled"
                            value={whatsappEnabled ? 'true' : 'false'}
                            onChange={(e) => setWhatsappEnabled(e.target.value === 'true')}
                            className="form-input"
                            disabled={!whatsappPhone.trim()}
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                        <small className="field-hint">
                            {whatsappPhone.trim()
                                ? 'Ao marcar Sim você registra o consentimento da pessoa — é ele que permite o envio automático.'
                                : 'Preencha o telefone para poder ligar o envio automático.'}
                        </small>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="telegramUsername">Telegram (@usuário) <span className="field-optional">(opcional)</span></label>
                        <input
                            id="telegramUsername"
                            type="text"
                            value={telegramUsername}
                            onChange={(e) => setTelegramUsername(e.target.value)}
                            className="form-input"
                            placeholder="@maria.silva"
                        />
                        <small className="field-hint">
                            Só para conferência. O vínculo de verdade é feito pelo convite,
                            na tela de edição depois de salvar.
                        </small>
                    </div>
                    <div className="form-group" />
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="password">Senha</label>
                        <input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="form-input"
                            placeholder="Digite a senha"
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="confirmPassword">Confirmação da Senha</label>
                        <input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => {
                                setConfirmPassword(e.target.value);
                                setPasswordMismatch(false);
                            }}
                            onBlur={() => {
                                if (confirmPassword && confirmPassword !== password) {
                                    setPasswordMismatch(true);
                                }
                            }}
                            className={`form-input ${passwordMismatch ? 'form-input-error' : ''}`}
                            placeholder="Confirme a senha"
                        />
                        {passwordMismatch && (
                            <span className="field-error">As senhas não coincidem.</span>
                        )}
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="role">Perfil</label>
                        <select
                            id="role"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                            className="form-input"
                            disabled={!!fixedRole}
                        >
                            {roles.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label}
                                </option>
                            ))}
                        </select>
                    </div>
                    {role === 'salesman' && (
                        <div className="form-group">
                            <label htmlFor="recordingsStart">Data de Início *</label>
                            <DateInputBR
                                id="recordingsStart"
                                value={recordingsStart}
                                onChange={setRecordingsStart}
                            />
                        </div>
                    )}
                    {role !== 'salesman' && <div className="form-group" />}
                </div>

                {role === 'salesman' && (
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="appVersionName">Versão do app</label>
                            <select
                                id="appVersionName"
                                value={appVersionName}
                                onChange={(e) => setAppVersionName(e.target.value)}
                                className="form-input"
                            >
                                <option value="default">default</option>
                                <option value="gamification">gamification</option>
                            </select>
                            <span className="field-hint">
                                Se você selecionar uma versão aqui, apenas este vendedor terá a versão escolhida do app — as demais lojas/vendedores continuam com a versão da loja.
                            </span>
                        </div>
                    </div>
                )}

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="feedbackReadEnabled">Receber Feedbacks?</label>
                        <select
                            id="feedbackReadEnabled"
                            value={feedbackReadEnabled ? 'true' : 'false'}
                            onChange={(e) => {
                                const enabled = e.target.value === 'true';
                                setFeedbackReadEnabled(enabled);
                                if (!enabled) setFeedbackReadStartDate('');
                            }}
                            className="form-input"
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                    </div>
                    {feedbackReadEnabled ? (
                        <div className="form-group">
                            <label htmlFor="feedbackReadStartDate">Data Início Feedbacks <span className="field-optional">(opcional)</span></label>
                            <DateInputBR
                                id="feedbackReadStartDate"
                                value={feedbackReadStartDate}
                                onChange={setFeedbackReadStartDate}
                            />
                        </div>
                    ) : (
                        <div className="form-group" />
                    )}
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="feedbackTelegramEnabled">Receber Feedbacks por Telegram?</label>
                        <select
                            id="feedbackTelegramEnabled"
                            value={feedbackTelegramEnabled ? 'true' : 'false'}
                            onChange={(e) => setFeedbackTelegramEnabled(e.target.value === 'true')}
                            className="form-input"
                        >
                            <option value="false">Não</option>
                            <option value="true">Sim</option>
                        </select>
                        {feedbackTelegramEnabled && (
                            <small className="field-hint">
                                Ao marcar Sim você registra o consentimento da pessoa.
                                {!MOSTRAR_FREQUENCIA && role && ` ${telegramEscopoHint(role, MODO_PADRAO)}`}
                            </small>
                        )}
                    </div>
                    <div className="form-group">
                        <label>O que a pessoa recebe</label>
                        <div className={`telegram-tipos${feedbackTelegramEnabled ? '' : ' telegram-tipos-off'}`}>
                            <label className="telegram-tipo">
                                <input
                                    type="checkbox"
                                    checked={feedbackTelegramShort}
                                    disabled={!feedbackTelegramEnabled}
                                    onChange={(e) => setFeedbackTelegramShort(e.target.checked)}
                                />
                                <span>Feedback curto</span>
                            </label>
                            <label className="telegram-tipo">
                                <input
                                    type="checkbox"
                                    checked={feedbackTelegramLong}
                                    disabled={!feedbackTelegramEnabled}
                                    onChange={(e) => setFeedbackTelegramLong(e.target.checked)}
                                />
                                <span>Feedback longo</span>
                            </label>
                        </div>
                        {!feedbackTelegramEnabled ? (
                            <small className="field-hint">
                                Ligue &quot;Receber Feedbacks por Telegram&quot; ao lado para escolher.
                            </small>
                        ) : (!feedbackTelegramShort && !feedbackTelegramLong) ? (
                            <small className="field-hint" style={{ color: '#c0392b' }}>
                                Sem marcar nenhum, a pessoa não recebe nada.
                            </small>
                        ) : feedbackTelegramLong ? (
                            <small className="field-hint">
                                O feedback longo só é gerado para alguns clientes — onde não houver,
                                simplesmente não sai nada.
                            </small>
                        ) : null}
                    </div>
                    {feedbackTelegramEnabled && MOSTRAR_FREQUENCIA ? (
                        <div className="form-group">
                            <label htmlFor="feedbackTelegramMode">Frequência</label>
                            <select
                                id="feedbackTelegramMode"
                                value={feedbackTelegramMode}
                                onChange={(e) => setFeedbackTelegramMode(e.target.value)}
                                className="form-input"
                            >
                                <option value="digest">Resumo diário</option>
                                <option value="each">A cada atendimento</option>
                            </select>
                            <small className="field-hint">{telegramEscopoHint(role, feedbackTelegramMode)}</small>
                        </div>
                    ) : (
                        <div className="form-group" />
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={() => navigate(backUrl)}
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={submitting}
                    >
                        {submitting ? 'Cadastrando...' : 'Cadastrar Usuário'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UserCreate;
